import { Fragment, useCallback, useEffect, useState } from "react";
import { Container, Row, Col, Card } from "reactstrap";
import { Breadcrumbs } from "../../../AbstractElements";
import {
  MaterialReactTable,
  useMaterialReactTable,
} from "material-react-table";
import { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { useNavigate } from "react-router";
import {
  getAllPrograms,
  setProgram,
} from "../../../store/slices/programsSlice";
import CreateProgramModal from "./components/createProgramModal copy";
import { useDebounce } from "../../../Hooks/useDebounce";

const ProgramsTable = () => {
  const history = useNavigate();

  const dispatch = useDispatch();
  const [data, setData] = useState([]);
  const [totalCount, setCount] = useState(0);
  const [isModalOpen, setModalOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const debouncedSearch = useDebounce(searchQuery, 300);
  const [sorting, setSorting] = useState([]);
  const [columnFilters, setColumnFilters] = useState([]);
  const columnFilterSearch = useDebounce(columnFilters, 300);
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);

  const columns = useMemo(
    () => [
      {
        accessorKey: "ID",
        header: "Program ID",
        size: 150,
      },
      {
        accessorKey: "client_id",
        header: "Client ID",
        size: 150,
      },
      {
        accessorKey: "name",
        header: "Program Name",
        size: 150,
      },
      {
        accessorKey: "description",
        header: "Description",
        size: 150,
        // description is saved from a rich-text editor, so it's stored as
        // HTML - strip tags for this plain-text table cell rather than
        // showing the raw markup or rendering it unsanitized.
        Cell: ({ cell }) => {
          const value = cell.getValue();
          if (!value) return "";
          return value.replace(/<[^>]*>/g, "").trim();
        },
      },
      {
        accessorKey: "slug_name",
        header: "Program Slug Name",
        size: 150,
      },
    ],
    [],
  );

  const fetchData = async (payload) => {
    const sortField = sorting.length > 0 ? sorting[0].id : undefined;
    const sortOrder = sorting.length > 0 ? (sorting[0].desc ? "desc" : "asc") : undefined;
    
    const queryPayload = {
      pagination: true,
      limit: pageSize,
      offset: page * pageSize,
      ...(sortField && { sortBy: sortField }),
      ...(sortOrder && { sortOrder: sortOrder }),
      ...payload,
    };
    
    const res = await dispatch(getAllPrograms(queryPayload));
    if (res?.payload) {
      const p = res.payload;
      setData(p.programsData || p.programs || p.data || p.result || []);
      setCount(p.count !== undefined ? p.count : (p.total !== undefined ? p.total : 0));
    }
  };

  useEffect(() => {
    fetchData({ searchQuery: debouncedSearch, filters: columnFilterSearch });
  }, [dispatch, page, pageSize, debouncedSearch, columnFilterSearch, sorting]);

  const getPrograms = () => {
    fetchData({ searchQuery: debouncedSearch, filters: columnFilterSearch });
  };

  const handlePageChange = (event) => {
    const newState = typeof event === "function" ? event({ pageIndex: page, pageSize: pageSize }) : event;
    setPage(newState.pageIndex);
    setPageSize(newState.pageSize);
  };

  const handleOnChangeSearch = (value) => {
    setSearchQuery(value);
    setPage(0);
  };

  const rowAction = (event, row) => {
    dispatch(setProgram(row.original));
    history(`/pages/program-details`);
  };

  const table = useMaterialReactTable({
    columns,
    data,
    muiTableBodyRowProps: ({ row }) => ({
      onClick: (event) => rowAction(event, row),
      sx: {
        cursor: "pointer",
      },
    }),
    initialState: { density: "compact" },
    globalFilter: searchQuery,
    onGlobalFilterChange: handleOnChangeSearch,
    manualPagination: true,
    manualSorting: true,
    manualFiltering: true,
    rowCount: totalCount || 0,
    onPaginationChange: handlePageChange,
    onSortingChange: setSorting,
    onColumnFiltersChange: setColumnFilters,
    state: {
      pagination: { pageIndex: page, pageSize: pageSize },
      sorting,
      columnFilters,
      globalFilter: searchQuery,
    },
    renderTopToolbarCustomActions: ({ table }) => (
      <div
        sx={{
          display: "flex",
          gap: "16px",
          padding: "8px",
          flexWrap: "wrap",
        }}
      >
        <button className="btn bg-primary" onClick={() => setModalOpen(true)}>
          Create Program
        </button>
      </div>
    ),
  });

  const toggle = useCallback(() => {
    setModalOpen(!isModalOpen);
  }, [isModalOpen]);

  return (
    <Fragment>
      <CreateProgramModal
        isOpen={isModalOpen}
        toggle={toggle}
        pagination={{ pageIndex: page, pageSize: pageSize }}
      ></CreateProgramModal>
      <Breadcrumbs mainTitle="Plans" parent="Plans" parentPath="programs" title="Plans List" />
      <Container fluid={true}>
        <Row>
          <Col sm="12">
            <Card>
              <MaterialReactTable table={table} />
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default ProgramsTable;
